import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticle, useArticles } from "@/hooks/useArticles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ArticleCard from "@/components/base/ArticleCard";
import FloatingShareBar from "@/components/base/FloatingShareBar";
import ArticleKeyPoints from "./components/ArticleKeyPoints";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

function ArticleSeo({ article }: { article: NonNullable<ReturnType<typeof useArticle>["article"]> }) {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";

  useSeoMeta({
    title: article.title,
    description: article.summary,
    image: article.image,
    url: window.location.href,
    type: "article",
    author: article.entrepreneur,
    publishedTime: article.date,
    section: article.category,
    keywords: `${article.entrepreneur}, ${article.sector}, ${article.country}, entrepreneur africain, Wazi Stories`,
  });

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.summary,
    "image": article.image,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Person",
      "name": article.entrepreneur
    },
    "publisher": {
      "@type": "Organization",
      "name": "Wazi Stories Afrique",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/vite.svg`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    },
    "articleSection": article.category,
    "keywords": `${article.entrepreneur}, ${article.sector}, ${article.country}, entrepreneur africain`,
    "about": {
      "@type": "Person",
      "name": article.entrepreneur,
      "jobTitle": "Entrepreneur",
      "nationality": article.country
    }
  }, `article-${article.id}`);

  return null;
}

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { article, loading } = useArticle(id);
  const { articles: related } = useArticles({ category: article?.category, limit: 4 });
  const relatedFiltered = related.filter((a) => a.id !== id).slice(0, 3);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdf8f3]">
        <Navbar />
        <div className="pt-20 animate-pulse">
          <div className="h-[40vh] md:h-[50vh] bg-gray-200"></div>
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-10">
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-100 rounded w-full mb-2"></div>
            <div className="h-4 bg-gray-100 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdf8f3]">
        <div className="text-center px-4">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{t("article.notFound")}</h1>
          <Link to="/" className="text-orange-500 hover:underline cursor-pointer">{t("article.backHome")}</Link>
        </div>
      </div>
    );
  }

  const getArticleSourceUrl = (): string => {
    if (article.originalUrl) return article.originalUrl;
    const domain = article.sourceUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
    return `https://www.google.com/search?q=${encodeURIComponent(article.title + " " + article.entrepreneur)}+site:${domain}`;
  };
  const articleUrl = getArticleSourceUrl();
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(article.title + " - Wazi Stories Afrique\n" + articleUrl)}`, "_blank");
  const shareLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(articleUrl)}`, "_blank");
  const shareFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`, "_blank");

  const hasEnrichedContent = (article.keyPoints && article.keyPoints.length > 0) || (article.impact && article.impact.length > 0) || article.quote;

  return (
    <div className="min-h-screen bg-[#fdf8f3]">
      <ArticleSeo article={article} />
      <Navbar />
      <FloatingShareBar title={article.title} sourceUrl={article.originalUrl || article.sourceUrl} />

      <div className="pt-16 md:pt-20">
        {/* Hero image */}
        <div className="relative h-[45vw] min-h-[220px] max-h-[520px] overflow-hidden">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-8">
            <div className="max-w-5xl mx-auto">
              <span className="inline-block bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-2 md:mb-3">
                {article.category}
              </span>
              <h1 className="text-lg sm:text-2xl md:text-4xl font-bold text-white font-serif leading-tight">
                {article.title}
              </h1>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:py-10">

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm text-gray-500 mb-5 md:mb-6 pb-5 md:pb-6 border-b border-gray-200">
            <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full">
              <i className="ri-map-pin-line text-orange-400"></i>
              {article.countryFlag} {article.country}
            </span>
            <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full">
              <i className="ri-price-tag-3-line text-orange-400"></i>
              {article.sector}
            </span>
            <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full">
              <i className="ri-user-line text-orange-400"></i>
              {article.entrepreneur}
            </span>
            {article.readTime && (
              <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full">
                <i className="ri-time-line text-orange-400"></i>
                {article.readTime} {t("article.readTime")}
              </span>
            )}
            {article.views && (
              <span className="flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-full">
                <i className="ri-eye-line text-orange-400"></i>
                {article.views.toLocaleString()} {t("article.views")}
              </span>
            )}
            {hasEnrichedContent && (
              <span className="flex items-center gap-1 bg-orange-50 text-orange-600 border border-orange-100 px-2.5 py-1 rounded-full font-semibold">
                <i className="ri-star-line text-orange-500"></i>
                {t("article.fullAnalysis")}
              </span>
            )}
          </div>

          {/* Two-column layout */}
          <div className={hasEnrichedContent ? "flex flex-col lg:flex-row gap-8" : ""}>

            <div className={hasEnrichedContent ? "flex-1 min-w-0" : ""}>

              {/* Summary */}
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6 md:mb-8 font-medium">
                {article.summary}
              </p>

              {/* Key Points — mobile */}
              {hasEnrichedContent && (
                <div className="lg:hidden">
                  <ArticleKeyPoints article={article} />
                </div>
              )}

              {/* Source CTA */}
              <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 md:p-6 mb-6 md:mb-8">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 flex-shrink-0 mt-0.5">
                    <i className="ri-newspaper-line text-orange-500 text-base"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">{t("article.source")}</p>
                    <p className="text-xs text-gray-500">
                      {t("article.sourceDesc")} <strong>{article.source}</strong>.
                    </p>
                  </div>
                </div>
                <a
                  href={articleUrl}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 md:px-6 py-2.5 md:py-3 rounded-full transition-colors cursor-pointer text-sm whitespace-nowrap"
                >
                  <i className="ri-external-link-line"></i>
                  {t("article.readFull")} {article.source}
                </a>
              </div>

              {/* Share buttons */}
              <div className="mb-8 md:mb-12">
                <p className="text-sm font-semibold text-gray-700 mb-3">{t("article.share")}</p>
                <div className="flex flex-wrap gap-2 md:gap-3">
                  <button
                    onClick={shareWhatsApp}
                    className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i>WhatsApp
                  </button>
                  <button
                    onClick={shareLinkedIn}
                    className="flex items-center gap-2 bg-[#0a66c2] hover:bg-[#0958a8] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-linkedin-line"></i>LinkedIn
                  </button>
                  <button
                    onClick={shareFacebook}
                    className="flex items-center gap-2 bg-[#1877f2] hover:bg-[#1464d2] text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-facebook-line"></i>Facebook
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar — desktop only */}
            {hasEnrichedContent && (
              <aside className="hidden lg:block w-80 flex-shrink-0">
                <div className="sticky top-24">
                  <ArticleKeyPoints article={article} />
                </div>
              </aside>
            )}
          </div>

          {/* Related articles */}
          {relatedFiltered.length > 0 && (
            <div className="border-t border-gray-200 pt-8 md:pt-12">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 font-serif mb-4 md:mb-6 flex items-center gap-2">
                <i className="ri-article-line text-orange-500"></i>
                {t("article.related")}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {relatedFiltered.map((a) => (
                  <ArticleCard key={a.id} article={a as Parameters<typeof ArticleCard>[0]["article"]} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
